import {
  ApplicationStatusText,
  ApplicationStatus,
  isAllYourDetailsFieldsComplete,
  isYourDetailsFieldsStarted,
  isAllYourExperienceFieldsComplete,
  isAllCompetencyQuestionsFieldsComplete,
  isCompetencyQuestionsFieldsStarted,
  isAllRefereeFieldsComplete
} from "@/components/onboarding/types";
import { Button } from "@/components/ui";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { H3, P } from "@/components/ui/typography";
import { useSupabase } from "@/hooks/useSupabase";
import { supabase } from "@/lib/supabase";
import { trpc } from "@/trpc/react";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { CheckCircle, XCircle } from "lucide-react-native";
import React, { useEffect } from "react";
import { Alert, TouchableOpacity, View, Text, Image, ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Toast from "react-native-toast-message";
import * as Linking from 'expo-linking';
import { addWeeks } from 'date-fns'


export default function OnboardingScreen() {

  const { signOut, user, refetchUser } = useSupabase();
  const insets = useSafeAreaInsets();
  const router = useRouter();

  const { emailConfirmed } = useLocalSearchParams<{
    emailConfirmed: string;
  }>();

  const { data: recruitmentData, refetch } = trpc.recruitment.getById.useQuery();

  const applicationStatus = recruitmentData ? ApplicationStatusText[recruitmentData.status as ApplicationStatus] : "";

  const isYourDetailsComplete = isAllYourDetailsFieldsComplete(recruitmentData);
  const isYourDetailsStarted = isYourDetailsFieldsStarted(recruitmentData);

  const isYourExperienceComplete = isAllYourExperienceFieldsComplete(recruitmentData);
  const isYourExperienceStarted = isYourDetailsFieldsStarted(recruitmentData);

  const isCompetencyQuestionsComplete = isAllCompetencyQuestionsFieldsComplete(recruitmentData);
  const isCompetencyQuestionsStarted = isCompetencyQuestionsFieldsStarted(recruitmentData);

  const isRefereeComplete = isAllRefereeFieldsComplete(recruitmentData);
  const isRefereeStarted = isCompetencyQuestionsFieldsStarted(recruitmentData);

  const isEmailConfirmed = user?.user_metadata.email_verified


  const { mutate: updateCrewRequest } = trpc.recruitment.submit.useMutation({
    onSuccess(_response) {
      refetch();
      Toast.show({
        type: 'success',
        text1: 'Your application has been submitted'
      });
    },
    onError(error) {
      console.error('Failed to submit application:', error);
      Toast.show({
        type: 'error',
        text1: 'Failed to submit application',
        text2: error.message || 'Please try again'
      });
    }
  });

  const { mutate: createTask } = trpc.task.createTask.useMutation({
    onSuccess(_response) {
    },
  });

  const { mutate: updateCrew } = trpc.crew.updateCrew.useMutation({
    onSuccess(_response) {
      console.log('Crew details updated successfully');
    },
    onError(error) {
      // Log error but don't show toast - application submission is the main goal
      // Crew details can be updated later if needed
      console.error('Failed to update crew details:', error);
      // Only log the error, don't disrupt the user flow
    }
  });

  const { mutate: deleteRecruitment } = trpc.recruitment.delete.useMutation({
    onSuccess(_response) {
      if (signOut) {
        signOut();
      }
    },
  });

  const { mutate: sendConfirmEmail, isPending } = trpc.auth.sendConfirmEmail.useMutation({
    onSuccess() {
      promptEmailConfirmationSent();
    },
    onError(error) {
      console.error(error);
      Alert?.alert("An error occurred. Please try again.");
    }
  });

  const { mutate: confirmEmail } = trpc.auth.confirmEmail.useMutation({
    onSuccess() {
      Toast.show({
        type: 'success',
        text1: 'Email confirmed'
      });
      if (refetchUser) {
        refetchUser();
      }
    },
    onError(error) {
      Toast.show({
        type: 'error',
        text1: 'Please try again'
      });
    }
  });

  async function onSubmit() {
    if (!recruitmentData) return;

    if (!isYourDetailsComplete || !isYourExperienceComplete ||
      !isCompetencyQuestionsComplete || !isRefereeComplete || !isEmailConfirmed
    ) {
      promptToCompleteSections();
      return;
    }

    updateCrewRequest(recruitmentData?.id);

    if (recruitmentData.crew_id) {
      // Only include fields that are actually defined
      const crewUpdateData: {
        id: number;
        name?: string;
        mobile?: string;
        address_line_1?: string;
        address_line_2?: string;
        address_line_3?: string;
        address_postcode?: string;
      } = {
        id: recruitmentData.crew_id,
      };

      if (recruitmentData.first_name && recruitmentData.last_name) {
        crewUpdateData.name = `${recruitmentData.first_name} ${recruitmentData.last_name}`;
      }
      if (recruitmentData.phone) {
        crewUpdateData.mobile = recruitmentData.phone;
      }
      if (recruitmentData.address_line_1) {
        crewUpdateData.address_line_1 = recruitmentData.address_line_1;
      }
      if (recruitmentData.address_line_2) {
        crewUpdateData.address_line_2 = recruitmentData.address_line_2;
      }
      if (recruitmentData.address_city) {
        crewUpdateData.address_line_3 = recruitmentData.address_city;
      }
      if (recruitmentData.address_postcode) {
        crewUpdateData.address_postcode = recruitmentData.address_postcode;
      }

      if (Object.keys(crewUpdateData).length > 1) {
        updateCrew({
          id: crewUpdateData.id,
          name: crewUpdateData.name || '',
          mobile: crewUpdateData.mobile || '',
          address_line_1: crewUpdateData.address_line_1 || '',
          address_line_2: crewUpdateData.address_line_2 || '',
          address_line_3: crewUpdateData.address_line_3 || '',
          address_postcode: crewUpdateData.address_postcode || ''
        });
      }
    } else {
      console.error('Cannot update crew: crew_id is missing');
      Toast.show({
        type: 'error',
        text1: 'Failed to update crew details',
        text2: 'Crew ID is missing'
      });
    }

    if (createTask) {
      createTask({
        title: `New Crew Application for ${recruitmentData.first_name} ${recruitmentData.last_name}`,
        description: `New Crew Application for ${recruitmentData.first_name} ${recruitmentData.last_name}`,
        priority: 1,
        tags: ["recruitment"],
        due_date: addWeeks(new Date(), 1),
        crew_id: recruitmentData.crew_id,
        state_name: "Open",
        type: "recruitment.new",
        // todo: might be a specific recruitment team
        team_id: 2
      });
    }
  }

  async function withdrawApplication() {
    if (recruitmentData) {
      if (deleteRecruitment) {
        deleteRecruitment(recruitmentData?.id);
      }
      await supabase.storage.from("recruitment").remove([`${recruitmentData.id}/cv`]);
    } else {
      if (signOut) {
        signOut();
      }
    }
  }

  async function confirmEmailSubmit() {
    if (!user) return;

    const resetPasswordURL = Linking.createURL("confirm-email");

    sendConfirmEmail({ email: user.email || '', redirect_url: resetPasswordURL });
  }

  function promptToCompleteSections() {
    Alert.alert(
      'Application not complete!',
      'You must complete all sections before submitting your application.',
      [
        { text: 'Ok', style: 'cancel' },

      ],
      { cancelable: false }
    );
  }

  function promptToLogout() {
    Alert.alert(
      'Log out?',
      user?.email
        ? `You are signed in as ${user.email}. Log out to sign in with a different email — anything you have filled in is saved against this one.`
        : 'Log out to sign in with a different email — anything you have filled in is saved against this one.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Log out',
          onPress: () => {
            if (signOut) {
              signOut({ clearBiometric: true });
            }
          },
        },
      ],
      { cancelable: false }
    );
  }

  function promptToWithdrawApplication() {
    Alert.alert(
      'Are you sure you want to withdraw your application?',
      'This will remove your application from the system.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Withdraw', onPress: () => withdrawApplication() },
      ],
      { cancelable: false }
    );
  }

  function promptEmailConfirmationSent() {
    Alert.alert(
      'Email confirmation sent!',
      'Please check your emails for a confirmation link.',
      [
        { text: 'Ok', style: 'cancel' },

      ],
      { cancelable: false }
    );
  }

  const renderSectionText = (detailsComplete: boolean, detailsStarted: boolean) => {
    if (detailsComplete) {
      return "Section Complete";
    } else if (detailsStarted) {
      return "Section Incomplete";
    } else {
      return "Section not started";
    }
  }

  const renderSectionIcon = (detailsComplete: boolean) => {
    if (detailsComplete) {
      return <CheckCircle width={30} height={30} strokeWidth={1} stroke={"green"} />
    } else {
      return <XCircle width={30} height={30} strokeWidth={1} stroke={"red"} />
    }
  }

  useEffect(() => {
    if (emailConfirmed === "true") {
      confirmEmail();
      router.setParams({ emailConfirmed: undefined });
    }
  });


  return (
    <ScrollView
      className="flex-1 bg-background"
      contentContainerStyle={{
        paddingTop: insets.top,
        paddingBottom: insets.bottom + 20,
      }}
    >
      <View className="gap-4">
        <View className="flex-row items-center border-b  " style={{ borderBottomColor: 'rgba(0,0,0,0.1)' }}>
          <Image
            source={require('@/assets/images/icon.png')}
            style={{ height: 80, width: 80 }}

          />
          <View>
            <H3 className='mt-3 '>Become a Crewsader </H3>
            <P className="mb-3">Application Status {applicationStatus}</P>
          </View>
        </View>

        <View className="px-4 flex gap-4">
          {recruitmentData?.status === 0 && (
            <>
              <P className="mb-3">Complete each section and then click submit application.</P>
              <P className="mb-3">You can come back at any time to check the status of your application.</P>
            </>
          )}
          {recruitmentData?.status === 1 && (
            <P className="mb-3">Your application has been submitted, we aim to contact you within 5 working days.</P>
          )}
          <TouchableOpacity onPress={() => confirmEmailSubmit()} disabled={isEmailConfirmed || isPending}>
            <Card className="border !border-gray-300">
              <View className="flex-row justify-between items-center">
                <CardHeader >
                  <View className="flex-row items-center gap-4">
                    {renderSectionIcon(isEmailConfirmed || false)}
                    <View className="">
                      <CardTitle className="font-light">Confirm Email</CardTitle>
                      <Text className="text-gray-400 font-light">{renderSectionText(isEmailConfirmed || false, false)}</Text>
                    </View>
                  </View>
                </CardHeader>
                <Ionicons name="chevron-forward" size={20} color="gray" className="text-white mr-2" />
              </View>
            </Card>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.navigate("/your-details")}>
            <Card className="border !border-gray-300">
              <View className="flex-row justify-between items-center">
                <CardHeader>
                  <View className="flex-row items-center gap-4">
                    {renderSectionIcon(isYourDetailsComplete)}
                    <View className="">
                      <CardTitle className="font-light">Your Details</CardTitle>
                      <Text className="text-gray-400 font-light">{renderSectionText(isYourDetailsComplete, isYourDetailsStarted)}</Text>
                    </View>
                  </View>
                </CardHeader>
                <Ionicons name="chevron-forward" size={20} color="gray" className="text-white mr-2" />
              </View>
            </Card>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.navigate("/your-experience")}>
            <Card className="border !border-gray-300">
              <View className="flex-row justify-between items-center">
                <CardHeader>
                  <View className="flex-row items-center gap-4">
                    {renderSectionIcon(isYourExperienceComplete)}
                    <View className="">
                      <CardTitle className="font-light">Your Experience</CardTitle>
                      <Text className="text-gray-400 font-light">{renderSectionText(isYourExperienceComplete, isYourExperienceStarted)}</Text>
                    </View>
                  </View>
                </CardHeader>
                <Ionicons name="chevron-forward" size={20} color="gray" className="text-white mr-2" />
              </View>
            </Card>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.navigate("/competency-questions")}>
            <Card className="border !border-gray-300">
              <View className="flex-row justify-between items-center">
                <CardHeader>
                  <View className="flex-row items-center gap-4">
                    {renderSectionIcon(isCompetencyQuestionsComplete)}
                    <View className="">
                      <CardTitle className="font-light">Competency Questions</CardTitle>
                      <Text className="text-gray-400 font-light">{renderSectionText(isCompetencyQuestionsComplete, isCompetencyQuestionsStarted)}</Text>
                    </View>
                  </View>
                </CardHeader>
                <Ionicons name="chevron-forward" size={20} color="gray" className="text-white mr-2" />
              </View>
            </Card>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.navigate("/referee-cv")}>
            <Card className="border !border-gray-300">
              <View className="flex-row justify-between items-center">
                <CardHeader>
                  <View className="flex-row items-center gap-4">
                    {renderSectionIcon(isRefereeComplete)}
                    <View className="">
                      <CardTitle className="font-light">CV and References</CardTitle>
                      <Text className="text-gray-400 font-light">{renderSectionText(isRefereeComplete, isRefereeStarted)}</Text>
                    </View>
                  </View>
                </CardHeader>
                <Ionicons name="chevron-forward" size={20} color="gray" className="text-white mr-2" />
              </View>
            </Card>
          </TouchableOpacity>
        </View>
      </View>
      <View className="px-4 flex gap-2 mt-4 mb-4" >
        {recruitmentData?.status === 0 && (
          <Button className='uppercase' onPress={() => onSubmit()} variant="crew">
            Submit application
          </Button>
        )}

        {user?.email && (
          <P className="mt-2 text-center text-gray-500">Signed in as {user.email}</P>
        )}

        <Button className='' onPress={() => promptToLogout()} variant="outline">
          Log out
        </Button>

        <Button className='' onPress={() => promptToWithdrawApplication()} variant="destructive">
          Withdraw application
        </Button>
      </View>
    </ScrollView>
  );
}